/**
 * The Hondo bar's decisions, kept pure so they can be tested without a microphone or a server:
 * what the record button means right now, what a press and release should do, and which plain
 * status word a sent note is at (Saved on this phone, Sending, Working on it, Needs a look, Filed).
 */
import type { CaptureStatus } from "../../contract";
import type { MicrophoneAccess, RecorderState } from "./audio";

/** Marks an English catalog key (collected by the i18n check). Translate it with t() where it is shown. */
const tr = (english: string) => english;

// ---------------------------------------------------------------- the microphone button

/**
 * What the record button does if pressed now.
 * - enable: first, a tap asks for microphone access. It never records or saves anything.
 * - hold: access is granted: hold to talk and let go to send, or tap to start and tap to stop.
 * - tap: setup was done before but the browser can't confirm access silently, so a tap starts
 *   (the browser may ask again). Holding through a permission prompt is never required.
 */
export type MicMode = "unsupported" | "checking" | "enable" | "requesting" | "blocked" | "unavailable" | "tap" | "hold" | "starting" | "recording" | "finishing";

export function micMode(supported: boolean, access: MicrophoneAccess, state: RecorderState): MicMode {
  if (!supported) return "unsupported";
  if (state === "recording") return "recording";
  if (state === "starting") return "starting";
  if (state === "finishing") return "finishing";
  if (state === "requesting") return "requesting";
  if (access === "checking") return "checking";
  if (access === "denied") return "blocked";
  if (access === "unavailable") return "unavailable";
  if (access === "granted") return "hold";
  if (access === "ready") return "tap";
  return "enable";
}

/** Only a granted microphone may start recording from a press. Setup is always its own tap. */
export const canHoldToRecord = (mode: MicMode): boolean => mode === "hold";

/** English labels; translate at the call site with t(). */
export const MIC_LABEL: Record<MicMode, string> = {
  unsupported: tr("Type the note instead"),
  checking: tr("Checking the microphone…"),
  enable: tr("Enable microphone"),
  requesting: tr("Allow access in your browser"),
  blocked: tr("Microphone blocked"),
  unavailable: tr("Check the microphone"),
  tap: tr("Tap to record"),
  hold: tr("Hold to talk"),
  starting: tr("Getting ready…"),
  recording: tr("Listening"),
  finishing: tr("Saving…"),
};

export const MIC_HINT: Record<MicMode, string> = {
  unsupported: tr("This browser can't record audio. Typing works the same."),
  checking: tr("Checking microphone access."),
  enable: tr("First, allow microphone access. This step won't record or save anything."),
  requesting: tr("Choose Allow in your browser, then press the microphone again when you're ready."),
  blocked: tr("Allow the microphone for this site in your browser's settings, then try again. You can also type."),
  unavailable: tr("Check that a microphone is connected and free, then try again. You can also type."),
  tap: tr("Tap to start. Your browser may ask about the microphone again."),
  hold: tr("Hold to talk and let go to send, or tap to start and stop."),
  starting: tr("Wait for the timer before you talk."),
  recording: tr("Say the tag, the place and what happened."),
  finishing: tr("Saving your voice note."),
};

// ---------------------------------------------------------------- press and release

/** How long a press on the phone's record button must last before it starts recording. Shorter is a tap. */
export const HOLD_DELAY_MS = 280;
/** A press held longer than this before letting go counts as hold-to-talk rather than a tap. */
export const HOLD_RELEASE_MS = 650;
/** Sliding a finger this far from where it started means "never mind". */
export const SLIDE_CANCEL_PX = 72;

export interface Press {
  at: number;
  x: number;
  y: number;
  /** A recording was already running when this press began (so this press stops it). */
  wasRecording: boolean;
  /** This press started a recording. */
  started: boolean;
  /** The finger slid away: letting go cancels instead of sending. */
  cancelArmed: boolean;
}

export const newPress = (at: number, x: number, y: number, wasRecording: boolean): Press => ({ at, x, y, wasRecording, started: false, cancelArmed: false });

export function slidAway(press: Press, x: number, y: number): boolean {
  return Math.hypot(x - press.x, y - press.y) > SLIDE_CANCEL_PX;
}

/**
 * What letting go means.
 * - send: stop and send the recording.
 * - cancel: drop it; nothing is saved.
 * - too-early: the microphone was still warming up during a hold. Cancel with a clear message
 *   rather than silently starting a late or empty clip.
 * - keep: a short tap that started recording; it keeps going until the next tap.
 * - open: a short tap on the phone's record button opens the Hondo bar.
 * - none: nothing to do.
 */
export type ReleaseAction = "send" | "cancel" | "too-early" | "keep" | "open" | "none";

export function releaseAction(press: Press, now: number, state: RecorderState, opts: { tapOpens?: boolean } = {}): ReleaseAction {
  if (press.cancelArmed) return state === "idle" || state === "finishing" ? "none" : "cancel";
  if (press.wasRecording) return state === "recording" ? "send" : "none";
  if (opts.tapOpens && !press.started) return "open";
  const held = now - press.at;
  const hold = opts.tapOpens ? press.started : held > HOLD_RELEASE_MS;
  if (state === "starting") return hold ? "too-early" : "keep";
  if (state === "recording") return hold ? "send" : "keep";
  return "none";
}

// ---------------------------------------------------------------- live status of a sent note

export type Stage = "saved" | "sending" | "working" | "needs_review" | "filed" | "answered" | "failed" | "discarded";

/** The order a note moves through, for the small progress dots. Final stages share the last step. */
export const STAGE_STEP: Record<Stage, number> = { saved: 0, sending: 1, working: 2, needs_review: 3, filed: 3, answered: 3, failed: 3, discarded: 3 };

export const STAGE_LABEL: Record<Stage, string> = {
  saved: tr("Saved on this phone"),
  sending: tr("Sending"),
  working: tr("Working on it"),
  needs_review: tr("Needs a look"),
  filed: tr("Filed"),
  answered: tr("Answered"),
  failed: tr("Needs attention"),
  discarded: tr("Not filed"),
};

export const STAGE_TONE: Record<Stage, "info" | "warn" | "good" | "bad" | "neutral"> = {
  saved: "warn", sending: "info", working: "info", needs_review: "warn", filed: "good", answered: "good", failed: "bad", discarded: "neutral",
};

export const isSettled = (stage: Stage) => STAGE_STEP[stage] === 3;

/** The English label for a stage. On a computer, "Saved on this device" instead of "phone". */
export function stageLabel(stage: Stage, wide: boolean): string {
  return stage === "saved" && wide ? tr("Saved on this device") : STAGE_LABEL[stage];
}

/**
 * Where a note is. `pending` is its copy in this browser's outbox (present until the server has it
 * and every file is uploaded); `status` is the server's capture status once it does.
 */
export function captureStage(pending: { state: "saved" | "sending" | "failed" } | null | undefined, status: CaptureStatus | null | undefined): Stage {
  if (pending) return pending.state === "sending" ? "sending" : "saved";
  switch (status) {
    case "uploading": return "sending";
    case "queued":
    case "processing": return "working";
    case "needs_review": return "needs_review";
    case "filed": return "filed";
    case "answered": return "answered";
    case "failed": return "failed";
    case "discarded": return "discarded";
    default: return "sending";
  }
}

// ---------------------------------------------------------------- attachments staged before sending

export interface Staged { id: string; name: string; mime: string; size: number; blob: Blob; url: string | null }

/** The capture kind for what is about to be sent. */
export function captureKind(text: string, staged: Array<Pick<Staged, "mime">>): "text" | "photo" | "file" | "mixed" {
  if (!staged.length) return "text";
  const images = staged.every((s) => s.mime.startsWith("image/"));
  if (images) return "photo";
  return staged.some((s) => s.mime.startsWith("image/")) || text.trim() ? "mixed" : "file";
}

export const MAX_ATTACHMENTS = 6;

export function formatElapsed(ms: number): string {
  return `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, "0")}`;
}
